import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TEST_NAME_MAP } from "../constants/testNameMap";
import { getExpiratoryCalibrationScalarFromRow, isExpiratoryFlowSensorCalibrationRow } from "../utils/preuseErrorDecode";
import "./PreuseReport.css";

/** Shown when payload / binary decode does not supply version strings */
const REPORT_VERSION_DEFAULTS = {
  systemVersion: "--",
  osVersion: "--",
  uiVersion: "--",
  backendVersion: "--",
  firmwareMasterVersion: "--",
  firmwareSlaveVersion: "--",
  firmwarePowerBoardVersion: "--",
};

const FILTER_ALL = "all";
const FILTER_PASSED = "passed";
const FILTER_FAILED = "failed";

/** Other tests: open detail only when failed or masked error ≠ 0 */
function shouldNavigateToDetail(row) {
  const err = Number(row.errorcode) >>> 0;
  const failed = !row.passed;
  if (isExpiratoryFlowSensorCalibrationRow(row)) return true;
  return failed || err !== 0;
}

function getTestLabel(row) {
  const id = row.name_id ?? row.testid;
  return TEST_NAME_MAP[id] || `TEST_${id}`;
}

function formatSubtitle(row) {
  if (isExpiratoryFlowSensorCalibrationRow(row)) {
    return `Value: ${getExpiratoryCalibrationScalarFromRow(row)}`;
  }
  if (row.raw_data !== undefined) {
    if (Array.isArray(row.raw_data)) {
      const arr = row.raw_data;
      if (arr.length === 0) return "—";
      if (arr.length <= 8) return `Data: ${arr.join(", ")}`;
      return `Data: ${arr.length} values · ${arr.slice(0, 4).join(", ")}, …`;
    }
    return String(row.raw_data);
  }
  const n = row.data?.[0]?.data;
  return n !== undefined && n !== null ? `Value: ${n}` : "—";
}

function formatErrorAddress(errorCode) {
  const n = Number(errorCode);
  if (!Number.isFinite(n) || n === 0) return "#000000";
  const hex = n.toString(16).toUpperCase();
  const padded = hex.length <= 6 ? hex.padStart(6, "0") : hex;
  return `#${padded}`;
}

function localDateTime(ms) {
  const d = new Date(ms);
  return d.toLocaleString([], { hour12: false });
}

function composeVersion(prefixA, valueA, prefixB, valueB) {
  const a = valueA || "--";
  const b = valueB || "--";
  if (a === "--" && b === "--") return "--";
  return `${prefixA}_${a}_${prefixB}_${b}`;
}

function composeFirmwareVersion(master, slave, powerBoard) {
  const m = master || "--";
  const s = slave || "--";
  const p = powerBoard || "--";
  if (m === "--" && s === "--" && p === "--") return "--";
  return `M_${m}_SL_${s}_PDB_${p}`;
}

/** @param {'network'|'speaker'|'lightning'|'chip'} variant */
function TestIcon({ variant }) {
  const stroke = "currentColor";
  const common = { width: 22, height: 22, fill: "none", strokeWidth: 1.4 };
  switch (variant) {
    case "network":
      return (
        <svg {...common} viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="2.2" stroke={stroke} />
          <circle cx="5.5" cy="8.5" r="1.5" stroke={stroke} />
          <circle cx="18.5" cy="8.5" r="1.5" stroke={stroke} />
          <circle cx="7" cy="17" r="1.5" stroke={stroke} />
          <circle cx="17" cy="17" r="1.5" stroke={stroke} />
          <path d="M7.4 15.8L10.2 13.8M13.9 13.9l2.9 2M13.9 10.6l4.9-3M13.9 13.9h-3M10.9 11.2l-4.9-3" stroke={stroke} />
        </svg>
      );
    case "speaker":
      return (
        <svg {...common} viewBox="0 0 24 24" aria-hidden>
          <path d="M5 10v4h4l5 4V6L9 10H5z" stroke={stroke} />
          <path d="M16 9q3 3 0 6M18 7q5 5 0 10" stroke={stroke} strokeLinecap="round" />
        </svg>
      );
    case "lightning":
      return (
        <svg {...common} viewBox="0 0 24 24" aria-hidden>
          <path d="M13 .5L5 13h7l-1 10.5L19 11h-6.5L13 .5z" stroke={stroke} strokeLinejoin="round" />
        </svg>
      );
    default:
      return (
        <svg {...common} viewBox="0 0 24 24" aria-hidden>
          <rect x="5.5" y="7.5" width="13" height="10" rx="1.2" stroke={stroke} />
          <path d="M8 10.5h8M8 13.5h5" stroke={stroke} />
        </svg>
      );
  }
}

function iconVariantFor(name) {
  const n = name.toUpperCase();
  if (/HMI|COMM|NVCB|M_TO|VCB|LINK|NET/.test(n)) return "network";
  if (/BUZZ|AUDIO|SOUND|SIREN/.test(n)) return "speaker";
  if (/POWER|AC_|BATTERY|VOLT|PDB|SUPPLY/.test(n)) return "lightning";
  return "chip";
}

function HeaderIcon() {
  return (
    <svg className="preuse-header__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="4" width="18" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 8h12M7 13h10" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

export default function PreuseReport({ data }) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const first = data[0];
  const machineUDI =
    first?.machineUDI ||
    (first?.foreignkey != null ? `FK_${first.foreignkey}` : null) ||
    "—";
  const systemVersion = first?.systemVersion ?? REPORT_VERSION_DEFAULTS.systemVersion;
  const osVersion = first?.osVersion ?? REPORT_VERSION_DEFAULTS.osVersion;
  const uiVersion = first?.uiVersion ?? REPORT_VERSION_DEFAULTS.uiVersion;
  const backendVersion = first?.backendVersion ?? REPORT_VERSION_DEFAULTS.backendVersion;
  const firmwareMasterVersion =
    first?.firmwareMasterVersion ?? REPORT_VERSION_DEFAULTS.firmwareMasterVersion;
  const firmwareSlaveVersion =
    first?.firmwareSlaveVersion ?? REPORT_VERSION_DEFAULTS.firmwareSlaveVersion;
  const firmwarePowerBoardVersion =
    first?.firmwarePowerBoardVersion ?? REPORT_VERSION_DEFAULTS.firmwarePowerBoardVersion;
  const firmwareVersion = composeFirmwareVersion(
    firmwareMasterVersion,
    firmwareSlaveVersion,
    firmwarePowerBoardVersion
  );
  const softwareVersion = composeVersion("UI", uiVersion, "BE", backendVersion);
  const times = data.map((r) => r.time).filter((t) => Number.isFinite(t));
  const minTime = times.length ? Math.min(...times) : Date.now();

  const passedCount = data.filter((d) => d.passed).length;
  const failedCount = data.length - passedCount;
  const filteredRows = data.filter((row) => {
    if (statusFilter === FILTER_PASSED) return Boolean(row.passed);
    if (statusFilter === FILTER_FAILED) return !row.passed;
    return true;
  });

  return (
    <div className="preuse-report">
      <header className="preuse-header">
        <HeaderIcon />
        <h1 className="preuse-header__title">PREUSE TESTS REPORT</h1>
      </header>

      <section className="preuse-panel">
        <div className="preuse-panel__row">
          <span className="preuse-panel__label">DEVICE INFO</span>
          <span className="preuse-panel__time">{localDateTime(minTime)}</span>
        </div>
        <div className="preuse-chip-row">
          <div className="preuse-chip">
            <span className="preuse-chip__k">SERIAL</span>
            <span className="preuse-chip__v">{machineUDI}</span>
          </div>
          <div className="preuse-chip">
            <span className="preuse-chip__k">SYSTEM VERSION</span>
            <span className="preuse-chip__v">{systemVersion}</span>
          </div>
          <div className="preuse-chip">
            <span className="preuse-chip__k">OS VERSION</span>
            <span className="preuse-chip__v">{osVersion}</span>
          </div>
          <div className="preuse-chip">
            <span className="preuse-chip__k">FIRMWARE VERSION</span>
            <span className="preuse-chip__v">{firmwareVersion}</span>
          </div>
          <div className="preuse-chip">
            <span className="preuse-chip__k">SOFTWARE VERSION</span>
            <span className="preuse-chip__v">{softwareVersion}</span>
          </div>
        </div>
      </section>

      <div className="preuse-log-title">HARDWARE DIAGNOSTIC LOG</div>
      <div className="preuse-filter-row" role="tablist" aria-label="Test status filter">
        <button
          type="button"
          role="tab"
          className={`preuse-filter-btn ${statusFilter === FILTER_ALL ? "preuse-filter-btn--active" : ""}`}
          aria-selected={statusFilter === FILTER_ALL}
          onClick={() => setStatusFilter(FILTER_ALL)}
        >
          ALL ({data.length})
        </button>
        <button
          type="button"
          role="tab"
          className={`preuse-filter-btn ${statusFilter === FILTER_PASSED ? "preuse-filter-btn--active" : ""}`}
          aria-selected={statusFilter === FILTER_PASSED}
          onClick={() => setStatusFilter(FILTER_PASSED)}
        >
          PASSED ({passedCount})
        </button>
        <button
          type="button"
          role="tab"
          className={`preuse-filter-btn ${statusFilter === FILTER_FAILED ? "preuse-filter-btn--active" : ""}`}
          aria-selected={statusFilter === FILTER_FAILED}
          onClick={() => setStatusFilter(FILTER_FAILED)}
        >
          FAILED ({failedCount})
        </button>
      </div>

      {filteredRows.map((row, i) => {
        const name = getTestLabel(row);
        const variant = iconVariantFor(name);
        const fail = !row.passed;
        const clickable = shouldNavigateToDetail(row);

        const cardProps = clickable
          ? {
              type: "button",
              onClick: () => navigate("/detail", { state: { row } }),
              "aria-label": fail ? `Open details for failed test ${name}` : `Open details for test ${name}`,
            }
          : {};

        const CardTag = clickable ? "button" : "article";

        return (
          <CardTag
            key={`${row.testid}-${row.time}-${i}`}
            className={`preuse-test-card ${fail ? "preuse-test-card--fail" : "preuse-test-card--pass"} ${clickable ? "preuse-test-card--clickable" : ""}`}
            {...cardProps}
          >
            <div className="preuse-test-card__icon">
              <TestIcon variant={variant} />
            </div>
            <div className="preuse-test-card__body">
              <div className="preuse-test-card__head">
                <div>
                  <h2 className="preuse-test-card__name">{name.replace(/_/g, " ")}</h2>
                  <p className="preuse-test-card__subtitle">{formatSubtitle(row)}</p>
                </div>
                <div className={`preuse-status-badge ${fail ? "preuse-status-badge--fail" : "preuse-status-badge--pass"}`}>
                  <span className="preuse-status-badge__dot" />
                  {fail ? "FAILED" : "PASS"}
                </div>
              </div>

              {fail && (Number(row.errorcode) >>> 0) !== 0 && (
                <div className="preuse-error-row">
                  <span className="preuse-error-label">ERROR ADDRESS</span>
                  <code className="preuse-error-code">{formatErrorAddress(row.errorcode)}</code>
                </div>
              )}
            </div>
          </CardTag>
        );
      })}
    </div>
  );
}
